import React, { useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useInsights } from 'contexts/insights'
import { useAccess } from 'contexts/access'
import { useCheckOrganizationUnitPermission } from 'contexts/unit-permissions'
import { compact } from 'utilities/array'
import Modal from 'modals/dialog'
import Paragraph from 'components/typography/paragraph'
import { UnorderedListSmall, ListItem } from 'components/typography/list'
import Message from 'components/message'
import { InlineButton } from 'components/button'
import ExcludedUsers from 'pages/insights/modals/excluded-users'

const CalculationInfo = ({ showUnitPermissionDisclaimer = false }) => {
    const { formatMessage } = useIntl()
    const { excludedUserCount } = useInsights()

    const {
        check,
        checkFeature
    } = useAccess()

    const checkOrganizationUnitPermission = useCheckOrganizationUnitPermission()

    const usersManage = check('users:manage') || checkOrganizationUnitPermission('unit:users:manage')
    const unitUsersManage = check('unit:users:manage')

    const excludeFromInsightsAvailable = checkFeature('insights-exclude-people') && (usersManage || unitUsersManage)

    const [showExcludedUsers, setShowExcludedUsers] = useState(false)

    const messages = compact([
        (excludeFromInsightsAvailable && !!excludedUserCount) && formatMessage({
            id: 'insights_message_calculation_info_count',
            defaultMessage: '{count, plural, =0 {} =1 {1 user has been manually excluded from insights and are not included in the numbers.} other {{count} users have been manually excluded from insights and are not included in the numbers.}}'
        }, {
            button: chunks => (
                <InlineButton
                    className="underline-reversed"
                    onClick={() => setShowExcludedUsers(true)}>
                    {chunks}
                </InlineButton>
            ),
            count: excludedUserCount
        }),
        (!usersManage && unitUsersManage) && formatMessage({
            id: 'insights_message_excluded_people_info_unit_permissions',
            defaultMessage: 'Only members of groups in which you manage users will show up as excluded to you.'
        }),
        showUnitPermissionDisclaimer && formatMessage({
            id: 'insights_message_calculation_info_unit_permissions',
            defaultMessage: 'Only members of groups in which you can see insights are available in the charts.'
        })
    ])

    return (
        <>
            <Modal.Header heading={formatMessage({
                id: 'insights_action_show_info',
                defaultMessage: 'What are the numbers based on?'
            })} />
            <Modal.Body>
                <Paragraph className="preserve-line-breaks">
                    <FormattedMessage
                        id="insights_calculation_info"
                        defaultMessage="Everyone with their first workday in the past and last workday in the future are included.\n\nWhen a first or last workday is missing, it will be interpreted as a date in the past and the future, respectively." />
                </Paragraph>
                {!!messages.length && (
                    <Message
                        type="info"
                        message={messages[0]}>
                        {(messages.length === 2) && (
                            <Paragraph className="compact">
                                {messages[1]}
                            </Paragraph>
                        )}
                        {(messages.length > 2) && (
                            <UnorderedListSmall className="compact">
                                {messages.slice(1).map(message => (
                                    <ListItem key={message}>
                                        {message}
                                    </ListItem>
                                ))}
                            </UnorderedListSmall>
                        )}
                    </Message>
                )}
            </Modal.Body>
            {(excludeFromInsightsAvailable && !!excludedUserCount) && (
                <Modal
                    show={!!showExcludedUsers}
                    dismiss={() => setShowExcludedUsers(false)}
                    className="scrollable">
                    <ExcludedUsers />
                </Modal>
            )}
        </>
    )
}

export default CalculationInfo